import { useMemo } from "react";
import { Box, Text } from "@radix-ui/themes";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useI18n } from "@/i18n";
import { formatDateDisplay, formatDateShort } from "@/lib/dates";
import { formatValue } from "@/lib/format";
import type { ObservationPoint, Unit } from "@/types";

interface SeriesChartProps {
  data: ObservationPoint[];
  unit: Unit;
}

interface ChartPoint {
  date: string;
  label: string;
  value: number;
}

export function SeriesChart({ data, unit }: SeriesChartProps) {
  const { t, locale } = useI18n();

  const chartData = useMemo<ChartPoint[]>(
    () => data.map((p) => ({ date: p.date, label: formatDateShort(p.date, locale), value: p.value })),
    [data, locale]
  );

  const [minValue, maxValue] = useMemo(() => {
    const values = data.map((p) => p.value);
    return [Math.min(...values), Math.max(...values)];
  }, [data]);

  const padding = Math.max((maxValue - minValue) * 0.1, (Math.abs(maxValue) || 1) * 0.01);
  const tickInterval = Math.max(0, Math.ceil(chartData.length / 10) - 1);

  return (
    <Box style={{ width: "100%", height: 380 }} className="rk-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-a5)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "var(--gray-11)", fontFamily: "var(--font-mono)" }}
            tickLine={{ stroke: "var(--gray-a5)" }}
            axisLine={{ stroke: "var(--gray-a5)" }}
            interval={tickInterval}
            minTickGap={24}
          />
          <YAxis
            domain={[minValue - padding, maxValue + padding]}
            tick={{ fontSize: 11, fill: "var(--gray-11)", fontFamily: "var(--font-mono)" }}
            tickLine={{ stroke: "var(--gray-a5)" }}
            axisLine={{ stroke: "var(--gray-a5)" }}
            tickFormatter={(value: number) => formatValue(value, unit)}
            width={72}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload || payload.length === 0) return null;
              const point = payload[0]?.payload as ChartPoint;
              return (
                <Box className="rk-chart-tooltip">
                  <Text as="div" size="2" weight="medium" className="rk-mono">
                    {formatValue(point.value, unit)}
                  </Text>
                  <Text as="div" size="1" color="gray">
                    {formatDateDisplay(point.date, locale)}
                  </Text>
                </Box>
              );
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke="var(--accent-9)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "var(--accent-9)" }}
            name={t("chart.value")}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
